import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'

const { FiMessageCircle, FiX, FiSend, FiLoader, FiBot, FiUser, FiMinus } = FiIcons

const AIChatbot = ({ 
  isEnabled = true, 
  config = {
    name: 'Assistant',
    welcomeMessage: 'Hello! How can I help you today?',
    position: 'bottom-right',
    primaryColor: '#2563eb',
    features: ['general-support', 'faq-answers'],
    customInstructions: '',
    knowledgeBase: '',
    maxTokens: 150,
    temperature: 0.7
  }
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: config.welcomeMessage,
      sender: 'bot',
      timestamp: new Date()
    }
  ])
  const [inputMessage, setInputMessage] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const messagesEndRef = useRef(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Don't render if not enabled
  if (!isEnabled) return null

  const getPositionClasses = () => {
    switch (config.position) {
      case 'bottom-left':
        return 'bottom-4 left-4'
      case 'top-right':
        return 'top-4 right-4'
      case 'top-left':
        return 'top-4 left-4'
      default:
        return 'bottom-4 right-4'
    }
  }

  const sendMessage = async () => {
    if (!inputMessage.trim()) return

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInputMessage('')
    setIsTyping(true)

    try {
      // Simulate AI response (in a real app, this would call your AI service)
      const response = await simulateAIResponse(inputMessage, config)
      
      const botMessage = {
        id: Date.now() + 1,
        text: response,
        sender: 'bot',
        timestamp: new Date()
      }

      setTimeout(() => {
        setMessages(prev => [...prev, botMessage])
        setIsTyping(false)
      }, 1000)

    } catch (error) {
      console.error('Error sending message:', error)
      const errorMessage = {
        id: Date.now() + 1,
        text: 'Sorry, I encountered an error. Please try again.',
        sender: 'bot',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, errorMessage])
      setIsTyping(false)
    }
  }

  const simulateAIResponse = async (message, config) => {
    // This is a simulation - in a real app, you'd call your AI service
    const responses = {
      'general-support': [
        "I'm here to help! What can I assist you with today?",
        "How can I help you with your question?",
        "I'd be happy to assist you. What do you need help with?"
      ],
      'faq-answers': [
        "That's a great question! Let me help you with that.",
        "I can help explain that for you.",
        "Here's what I can tell you about that..."
      ]
    }

    // Simple keyword matching (in real implementation, use proper AI)
    const lowerMessage = message.toLowerCase()
    
    if (lowerMessage.includes('help') || lowerMessage.includes('support')) {
      return responses['general-support'][Math.floor(Math.random() * responses['general-support'].length)]
    }
    
    if (lowerMessage.includes('how') || lowerMessage.includes('what') || lowerMessage.includes('?')) {
      return responses['faq-answers'][Math.floor(Math.random() * responses['faq-answers'].length)]
    }

    // Default responses
    const defaultResponses = [
      "Thank you for your message! I understand you're asking about " + message.split(' ').slice(0, 3).join(' ') + ". How can I help you further?",
      "I see you're interested in " + message.split(' ')[0] + ". What specific information would you like to know?",
      "That's an interesting question about " + message.split(' ').slice(-3).join(' ') + ". Let me help you with that.",
    ]

    return defaultResponses[Math.floor(Math.random() * defaultResponses.length)]
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <div className={`fixed ${getPositionClasses()} z-50`}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            className={`mb-4 bg-white rounded-2xl shadow-2xl border border-slate-200 ${
              isMinimized ? 'w-80 h-16' : 'w-80 h-96'
            } transition-all duration-300`}
          >
            {/* Header */}
            <div 
              className="flex items-center justify-between p-4 border-b border-slate-200 rounded-t-2xl cursor-pointer"
              style={{ backgroundColor: config.primaryColor }}
              onClick={() => setIsMinimized(!isMinimized)}
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiBot} className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{config.name}</h3>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-white/80 text-xs">Online</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsMinimized(!isMinimized)
                  }}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiMinus} className="w-4 h-4 text-white" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsOpen(false)
                  }}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            {/* Chat Messages */}
            {!isMinimized && (
              <>
                <div className="flex-1 p-4 h-64 overflow-y-auto">
                  <div className="space-y-3">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-xs px-3 py-2 rounded-lg ${
                            message.sender === 'user'
                              ? 'bg-blue-600 text-white'
                              : 'bg-slate-100 text-slate-800'
                          }`}
                        >
                          <p className="text-sm">{message.text}</p>
                          <p className={`text-xs mt-1 ${
                            message.sender === 'user' ? 'text-blue-100' : 'text-slate-500'
                          }`}>
                            {message.timestamp.toLocaleTimeString([], { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                    
                    {isTyping && (
                      <div className="flex justify-start">
                        <div className="bg-slate-100 text-slate-800 px-3 py-2 rounded-lg">
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                            <span className="text-sm">{config.name} is typing...</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-4 border-t border-slate-200">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your message..."
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!inputMessage.trim() || isTyping}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <SafeIcon icon={FiSend} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Toggle Button */}
      {!isOpen && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsOpen(true)}
          className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white relative"
          style={{ backgroundColor: config.primaryColor }}
        >
          <SafeIcon icon={FiMessageCircle} className="w-6 h-6" />
          
          {/* Notification Badge */}
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
            <span className="text-xs text-white font-bold">!</span>
          </div>
        </motion.button>
      )}
    </div>
  )
}

export default AIChatbot